/**
 * ProjectCreateForm-new.tsx
 * 4-step project creation wizard.
 * Step 1 — Project Basics
 * Step 2 — Domain Knowledge (optional)
 * Step 3 — Enterprise Knowledge (optional)
 * Step 4 — Review & Create
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import {
    ArrowLeft,
    CheckCircleFill,
    CloudArrowUpFill,
    FolderFill,
    X,
} from 'react-bootstrap-icons';
import {
    CreateProjectPayload,
    ProjectType,
    WizardEnterpriseKnowledge,
} from '../../types/project';
import { parseEnterpriseAcronymsText } from '../../utils/enterpriseAcronyms';
import {
    PROJECT_WIZARD_SHOW_AZURE_EVENT,
    PROJECT_WIZARD_STEP_CHANGED_EVENT,
    PROJECT_WIZARD_STEP_EVENT,
} from '../../tour/GuidedTourContext';

/* ============================================================
   Public wizard data type (imported by the page wrapper)
   ============================================================ */

export interface WizardFinalData
{
    projectPayload: CreateProjectPayload;
    domainFiles: File[];
    enterpriseKnowledge: WizardEnterpriseKnowledge | null;
    enterpriseFiles: File[];
}

/* ============================================================
   Props interface
   ============================================================ */

interface ProjectCreateFormNewProps
{
    /** Called with all collected wizard data when the user submits on step 4 */
    onWizardComplete: (data: WizardFinalData) => Promise<void>;
    /** Whether the parent is currently submitting — disables all inputs */
    submitting?: boolean;
}

/* ============================================================
   Azure DevOps inline SVG icon
   Renders the Azure DevOps logo, colour-aware of selected state.
   ============================================================ */
interface AzureIconProps
{
    /** Fill colour for the SVG paths */
    fill: string;
}

/**
 * AzureDevOpsIcon
 * Accurate inline SVG of the Azure DevOps logo.
 * Uses a combined path with evenodd fill-rule so the inner
 * triangular region is cut out, matching the official branding mark.
 */
function AzureDevOpsIcon({ fill }: AzureIconProps): React.ReactElement
{
    return (
        <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            {/*
             * Outer polygon + inner triangular cutout combined in one path.
             * fillRule="evenodd" makes the second sub-path a transparent hole,
             * creating the distinctive Azure DevOps "fold" appearance.
             */}
            <path
                d="M0 13.186l3.183-4.103V4.648L8.6 2.722h.005L15 7.974V2.048L18.23 1.5 24 10.253l-2.726 1.142v8.007L14.877 22.5l-7.636-2.779-.454 2.396-3.637-.397-.363-5.216ZM14.877 19.98V8.7L9.422 6.25 6.694 13.126Z"
                fill={fill}
                fillRule="evenodd"
                clipRule="evenodd"
            />
        </svg>
    );
}

/* ============================================================
   ProjectTypeCard — a single clickable project-type selector card
   ============================================================ */
interface ProjectTypeCardProps
{
    /** Whether this card is the currently selected type */
    selected: boolean;
    /** Called when the card is clicked or activated via keyboard */
    onSelect: () => void;
    /** Display label shown below the icon */
    label: string;
    /** Icon element rendered inside the card */
    icon: React.ReactNode;
    /** Accessible ID for the card element */
    cardId: string;
}

/**
 * ProjectTypeCard
 * Clickable card used to select a project type.
 * Highlights in dark navy when selected.
 */
function ProjectTypeCard(
    { selected, onSelect, label, icon, cardId }: ProjectTypeCardProps
): React.ReactElement
{
    /** Handle keyboard activation (Enter or Space) */
    function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void
    {
        if (e.key === 'Enter' || e.key === ' ')
        {
            e.preventDefault();
            onSelect();
        }
    }

    return (
        <div
            id={cardId}
            className={`project-type-card${selected ? ' project-type-card-selected' : ''}`}
            onClick={onSelect}
            onKeyDown={handleKeyDown}
            role="radio"
            aria-checked={selected}
            tabIndex={0}
        >
            {/* Selection indicator — top-right corner */}
            <div className="project-type-card-indicator">
                {selected
                    ? <CheckCircleFill size={18} className="project-type-card-check" />
                    : <div className="project-type-card-circle" />
                }
            </div>

            {/* Icon */}
            <div className="project-type-card-icon">
                {icon}
            </div>

            {/* Label */}
            <div className="project-type-card-label">{label}</div>
        </div>
    );
}

/* ============================================================
   WizardStepBar — horizontal step indicator
   ============================================================ */

const STEP_LABELS = ['Basics', 'Domain Knowledge', 'Enterprise Knowledge', 'Review'];
const KNOWLEDGE_FILE_ACCEPT = '.pdf,.docx,.txt,.md,.markdown';
const KNOWLEDGE_FILE_TYPE_LABEL = 'PDF, DOCX, TXT, or MD';
const KNOWLEDGE_FILE_EXTENSIONS = new Set(['pdf', 'docx', 'txt', 'md', 'markdown']);

interface WizardStepBarProps
{
    currentStep: number;
}

function WizardStepBar({ currentStep }: WizardStepBarProps): React.ReactElement
{
    return (
        <div className="wizard-step-bar">
            {STEP_LABELS.map((label, index) =>
            {
                const stepNum = index + 1;
                const isActive = stepNum === currentStep;
                const isCompleted = stepNum < currentStep;

                return (
                    <React.Fragment key={stepNum}>
                        {index > 0 && (
                            <div className={`wizard-step-connector${isCompleted ? ' completed' : ''}`} />
                        )}
                        <div className="wizard-step">
                            <div className={`wizard-step-circle${isActive ? ' active' : ''}${isCompleted ? ' completed' : ''}`}>
                                {isCompleted ? <CheckCircleFill size={16} /> : stepNum}
                            </div>
                            <div className={`wizard-step-label${isActive ? ' active' : ''}${isCompleted ? ' completed' : ''}`}>
                                {label}
                            </div>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
}

/* ============================================================
   FileDropZone — drag-or-click file upload area
   ============================================================ */

const MAX_FILES = 5;
const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20 MB

function formatFileSize(bytes: number): string
{
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface FileDropZoneProps
{
    files: File[];
    onFilesChange: (files: File[]) => void;
    disabled?: boolean;
    dropZoneId?: string;
}

function FileDropZone({ files, onFilesChange, disabled, dropZoneId }: FileDropZoneProps): React.ReactElement
{
    const inputRef = useRef<HTMLInputElement>(null);
    const [dropError, setDropError] = useState<string | null>(null);

    function validateAndAdd(incoming: FileList | File[]): void
    {
        const arr = Array.from(incoming);
        const errors: string[] = [];
        const valid: File[] = [];

        for (const file of arr)
        {
            const ext = file.name.split('.').pop()?.toLowerCase();
            if (!ext || !KNOWLEDGE_FILE_EXTENSIONS.has(ext))
            {
                errors.push(`"${file.name}" is not a ${KNOWLEDGE_FILE_TYPE_LABEL} file.`);
                continue;
            }
            if (file.size === 0)
            {
                errors.push(`"${file.name}" is empty.`);
                continue;
            }
            if (file.size > MAX_FILE_BYTES)
            {
                errors.push(`"${file.name}" exceeds 20 MB.`);
                continue;
            }
            valid.push(file);
        }

        const combined = [...files, ...valid];
        if (combined.length > MAX_FILES)
        {
            errors.push(`Maximum ${MAX_FILES} files allowed.`);
            onFilesChange(combined.slice(0, MAX_FILES));
        }
        else
        {
            onFilesChange(combined);
        }

        setDropError(errors.length > 0 ? errors.join(' ') : null);
    }

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>): void
    {
        if (e.target.files)
        {
            validateAndAdd(e.target.files);
        }
        /* Reset input value so the same file can be added again after removal */
        if (inputRef.current)
        {
            inputRef.current.value = '';
        }
    }

    function handleDrop(e: React.DragEvent<HTMLDivElement>): void
    {
        e.preventDefault();
        validateAndAdd(e.dataTransfer.files);
    }

    function handleDragOver(e: React.DragEvent<HTMLDivElement>): void
    {
        e.preventDefault();
    }

    function removeFile(index: number): void
    {
        onFilesChange(files.filter((_, i) => i !== index));
        setDropError(null);
    }

    return (
        <div>
            <div
                id={dropZoneId}
                className="knowledge-dropzone"
                onClick={() => !disabled && inputRef.current?.click()}
                onDrop={disabled ? undefined : handleDrop}
                onDragOver={disabled ? undefined : handleDragOver}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) =>
                {
                    if ((e.key === 'Enter' || e.key === ' ') && !disabled)
                    {
                        inputRef.current?.click();
                    }
                }}
                aria-label={`Upload ${KNOWLEDGE_FILE_TYPE_LABEL} files`}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept={KNOWLEDGE_FILE_ACCEPT}
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleInputChange}
                    disabled={disabled}
                />
                <div className="knowledge-dropzone-icon">
                    <CloudArrowUpFill size={24} />
                </div>
                <div className="knowledge-dropzone-text">
                    Drag &amp; drop or click to upload {KNOWLEDGE_FILE_TYPE_LABEL}
                </div>
            </div>

            {dropError && (
                <div className="text-danger mt-2" style={{ fontSize: '0.8rem' }}>
                    {dropError}
                </div>
            )}

            {files.length > 0 && (
                <div className="knowledge-file-list">
                    {files.map((file, index) => (
                        <div key={index} className="knowledge-file-item">
                            <span className="knowledge-file-name">{file.name}</span>
                            <span className="knowledge-file-size">{formatFileSize(file.size)}</span>
                            <button
                                type="button"
                                className="knowledge-file-remove"
                                onClick={() => removeFile(index)}
                                disabled={disabled}
                                aria-label={`Remove ${file.name}`}
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ============================================================
   Main form component
   ============================================================ */

/**
 * ProjectCreateFormNew
 * 4-step wizard for creating a project.
 */
function ProjectCreateFormNew(
    { onWizardComplete, submitting = false }: ProjectCreateFormNewProps
): React.ReactElement
{
    const navigate = useNavigate();

    /* ---------- Step navigation ---------- */
    const [currentStep, setCurrentStep] = useState<number>(1);

    /* ---------- Step 1: Project Basics ---------- */
    const [name, setName] = useState<string>('');
    const [domainDescription, setDomainDescription] = useState<string>('');
    const [projectType, setProjectType] = useState<ProjectType>('simple');
    const [azureOrg, setAzureOrg] = useState<string>('');
    const [azureProject, setAzureProject] = useState<string>('');
    const [azureTeam, setAzureTeam] = useState<string>('');
    const [azurePAT, setAzurePAT] = useState<string>('');
    const [showAzureFieldsForTour, setShowAzureFieldsForTour] = useState<boolean>(false);
    const [step1Error, setStep1Error] = useState<string | null>(null);

    /* ---------- Step 2: Domain Knowledge ---------- */
    const [step2Notes, setStep2Notes] = useState<string>('');
    const [domainFiles, setDomainFiles] = useState<File[]>([]);

    /* ---------- Step 3: Enterprise Knowledge ---------- */
    const [entSummary, setEntSummary] = useState<string>('');
    const [entSystems, setEntSystems] = useState<string>('');
    const [entAcronyms, setEntAcronyms] = useState<string>('');
    const [entProcessNotes, setEntProcessNotes] = useState<string>('');
    const [entTestingNotes, setEntTestingNotes] = useState<string>('');
    const [enterpriseFiles, setEnterpriseFiles] = useState<File[]>([]);

    /* ---------- Helpers ---------- */

    function goToStep(step: number, source: 'user' | 'tour' = 'user'): void
    {
        setCurrentStep(step);
        if (source === 'user' && (step === 1 || step === 2 || step === 3 || step === 4))
        {
            window.dispatchEvent(new CustomEvent(PROJECT_WIZARD_STEP_CHANGED_EVENT, {
                detail: { step },
            }));
        }
    }

    useEffect(() =>
    {
        function handleGuidedTourStep(event: Event): void
        {
            const detail = (event as CustomEvent<{ step?: number }>).detail;
            if (typeof detail?.step === 'number')
            {
                goToStep(detail.step, 'tour');
            }
        }

        window.addEventListener(PROJECT_WIZARD_STEP_EVENT, handleGuidedTourStep);
        return () => window.removeEventListener(PROJECT_WIZARD_STEP_EVENT, handleGuidedTourStep);
    }, []);

    useEffect(() =>
    {
        function handleShowAzureDevOpsFields(): void
        {
            setShowAzureFieldsForTour(true);
            setStep1Error(null);
        }

        window.addEventListener(PROJECT_WIZARD_SHOW_AZURE_EVENT, handleShowAzureDevOpsFields);
        return () => window.removeEventListener(PROJECT_WIZARD_SHOW_AZURE_EVENT, handleShowAzureDevOpsFields);
    }, []);

    function validateStep1(): boolean
    {
        if (!name.trim())
        {
            setStep1Error('Project name is required.');
            return false;
        }
        if (projectType === 'azure_devops')
        {
            if (!azureOrg.trim() || !azureProject.trim() || !azureTeam.trim())
            {
                setStep1Error('Azure organization, project, and team are required.');
                return false;
            }
            if (!azurePAT.trim())
            {
                setStep1Error('Azure DevOps Personal Access Token is required.');
                return false;
            }
        }
        setStep1Error(null);
        return true;
    }

    function handleStep1Next(): void
    {
        if (validateStep1())
        {
            goToStep(2);
        }
    }

    /* --- Final submission --- */

    async function handleFinalSubmit(): Promise<void>
    {
        if (!validateStep1())
        {
            goToStep(1);
            return;
        }

        const combined = domainDescription.trim() + (step2Notes.trim() ? '\n\n' + step2Notes.trim() : '');
        const parsedAcronyms = parseEnterpriseAcronymsText(entAcronyms);

        const projectPayload: CreateProjectPayload = {
            name: name.trim(),
            domain_description: combined || undefined,
            project_type: projectType,
        };

        if (projectType === 'azure_devops')
        {
            projectPayload.azure_devops_org = azureOrg.trim();
            projectPayload.azure_devops_project = azureProject.trim();
            projectPayload.azure_devops_team = azureTeam.trim();
            projectPayload.azure_devops_pat = azurePAT;
        }

        const hasEnterprise = !!(
            entSummary.trim() ||
            entSystems.trim() ||
            parsedAcronyms.length > 0 ||
            entProcessNotes.trim() ||
            entTestingNotes.trim()
        );

        const enterpriseKnowledge: WizardEnterpriseKnowledge | null = hasEnterprise
            ? {
                summary: entSummary.trim(),
                internal_systems: entSystems.split('\n').map(s => s.trim()).filter(Boolean),
                acronyms: parsedAcronyms,
                process_notes: entProcessNotes.trim(),
                testing_notes: entTestingNotes.trim(),
            }
            : null;

        await onWizardComplete({
            projectPayload,
            domainFiles,
            enterpriseKnowledge,
            enterpriseFiles,
        });
    }

    /* ================================================================
       Step renders
       ================================================================ */

    /* Derived colour values used in Step 1 and Step 4 */
    const simpleFolderColor = projectType === 'simple' ? '#93c5fd' : '#6b7280';
    const azureIconFill = projectType === 'azure_devops' ? '#ffffff' : '#0078D4';
    const showAzureDevOpsConfig = projectType === 'azure_devops' || showAzureFieldsForTour;

    /* --- Step 1 --- */

    function renderStep1(): React.ReactElement
    {
        return (
            <>
                <div className="wizard-section-title">Project Basics</div>
                <div className="wizard-section-subtitle">
                    Enter the core details for your new project.
                </div>

                {step1Error && (
                    <Alert variant="danger" className="py-2 mb-3">
                        {step1Error}
                    </Alert>
                )}

                <Form.Group id="project-create-name" controlId="project-create-name-input" className="mb-4">
                    <Form.Label className="project-create-label">Project Name</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Name your project"
                        value={name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                        disabled={submitting}
                        className="project-create-input"
                    />
                </Form.Group>

                <Form.Group id="project-domain-description" controlId="project-domain-description-input" className="mb-4">
                    <Form.Label className="project-create-label">Business and Application Context</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        placeholder="Describe the business domain, application goals, users, workflows, and any project context that should shape generated domain knowledge"
                        value={domainDescription}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDomainDescription(e.target.value)}
                        disabled={submitting}
                        className="project-create-input"
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.78rem' }}>
                        This becomes the starting context for generated domain knowledge and works best when paired with uploaded project documents.
                    </Form.Text>
                </Form.Group>

                <Form.Group id="project-create-type" controlId="projectType" className="mb-4">
                    <Form.Label className="project-create-label">Project Type</Form.Label>

                    <div className="project-type-cards" role="radiogroup" aria-label="Project type">
                        <ProjectTypeCard
                            cardId="project-type-simple"
                            selected={projectType === 'simple'}
                            onSelect={() => { setProjectType('simple'); setShowAzureFieldsForTour(false); setStep1Error(null); }}
                            label="Simple Project"
                            icon={<FolderFill size={28} color={simpleFolderColor} />}
                        />
                        <ProjectTypeCard
                            cardId="project-type-azure"
                            selected={projectType === 'azure_devops'}
                            onSelect={() => { setProjectType('azure_devops'); setShowAzureFieldsForTour(false); setStep1Error(null); }}
                            label="Azure DevOps"
                            icon={<AzureDevOpsIcon fill={azureIconFill} />}
                        />
                    </div>
                </Form.Group>

                {showAzureDevOpsConfig && (
                    <div className="azure-devops-config mb-4">
                        <h6 className="azure-devops-config-title">Azure DevOps Configuration</h6>

                        <Form.Group id="project-create-azure-org" controlId="project-create-azure-org-input" className="mb-3">
                            <Form.Label className="project-create-label">Organization</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="my-organization"
                                value={azureOrg}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAzureOrg(e.target.value)}
                                disabled={submitting}
                                className="project-create-input"
                            />
                        </Form.Group>

                        <Form.Group id="project-create-azure-project" controlId="project-create-azure-project-input" className="mb-3">
                            <Form.Label className="project-create-label">Project</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="MyProject"
                                value={azureProject}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAzureProject(e.target.value)}
                                disabled={submitting}
                                className="project-create-input"
                            />
                        </Form.Group>

                        <Form.Group id="project-create-azure-team" controlId="project-create-azure-team-input" className="mb-3">
                            <Form.Label className="project-create-label">Team</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="MyTeam"
                                value={azureTeam}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAzureTeam(e.target.value)}
                                disabled={submitting}
                                className="project-create-input"
                            />
                        </Form.Group>

                        <Form.Group id="project-create-azure-pat" controlId="project-create-azure-pat-input">
                            <Form.Label className="project-create-label">
                                Personal Access Token
                            </Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="Enter your PAT"
                                value={azurePAT}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAzurePAT(e.target.value)}
                                disabled={submitting}
                                className="project-create-input"
                            />
                            <Form.Text className="text-muted" style={{ fontSize: '0.78rem' }}>
                                Your PAT is encrypted per project and never stored in plain text.
                            </Form.Text>
                        </Form.Group>
                    </div>
                )}

                <div className="wizard-step-footer">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => navigate('/projects')}
                        disabled={submitting}
                        className="project-create-back-btn"
                    >
                        <ArrowLeft size={13} className="me-1" />
                        Back to Projects
                    </Button>
                    <Button
                        id="project-create-step1-next"
                        variant="primary"
                        onClick={handleStep1Next}
                        disabled={!name.trim() || submitting}
                        className="project-create-submit-btn"
                    >
                        Next →
                    </Button>
                </div>
            </>
        );
    }

    /* --- Step 2 --- */

    function renderStep2(): React.ReactElement
    {
        return (
            <>
                <div className="wizard-section-title">Domain Knowledge</div>
                <div className="wizard-section-subtitle">
                    This step is optional. Add domain context that will be used to generate AI domain knowledge for your project.
                </div>

                <Form.Group id="project-create-domain-notes" controlId="project-create-domain-notes-input" className="mb-4">
                    <Form.Label className="project-create-label">Domain Context Notes</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        placeholder="Add any additional domain context beyond the business description in Step 1, e.g. technical constraints, regulatory context, target users"
                        value={step2Notes}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setStep2Notes(e.target.value)}
                        disabled={submitting}
                        className="project-create-input"
                    />
                </Form.Group>

                <div id="project-create-domain-documents" className="mb-1">
                    <Form.Label className="project-create-label mb-0">Upload Documents</Form.Label>
                    <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                        PDF, DOCX, TXT, or Markdown files that describe your domain, business rules, or architecture.
                    </p>
                    <FileDropZone
                        files={domainFiles}
                        onFilesChange={setDomainFiles}
                        disabled={submitting}
                        dropZoneId="project-create-domain-documents-dropzone"
                    />
                </div>

                <div className="wizard-step-footer">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => goToStep(1)}
                        disabled={submitting}
                        className="project-create-back-btn"
                    >
                        ← Back
                    </Button>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Button
                            id="project-create-step2-next"
                            variant="primary"
                            onClick={() => goToStep(3)}
                            disabled={submitting}
                            className="project-create-submit-btn"
                        >
                            Next →
                        </Button>
                        <button
                            type="button"
                            className="wizard-skip-link"
                            onClick={() => goToStep(3)}
                            disabled={submitting}
                        >
                            Skip this step →
                        </button>
                    </div>
                </div>
            </>
        );
    }

    /* --- Step 3 --- */

    function renderStep3(): React.ReactElement
    {
        return (
            <>
                <div className="wizard-section-title">Enterprise Knowledge</div>
                <div className="wizard-section-subtitle">
                    This step is optional. Add internal knowledge that is specific to your organization and not publicly available.
                </div>

                <Form.Group id="project-create-enterprise-overview" controlId="project-create-enterprise-overview-input" className="mb-4">
                    <Form.Label className="project-create-label">Overview</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Brief summary of your organization's internal context relevant to this project"
                        value={entSummary}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEntSummary(e.target.value)}
                        disabled={submitting}
                        className="project-create-input"
                    />
                </Form.Group>

                <Form.Group id="project-create-enterprise-systems" controlId="project-create-enterprise-systems-input" className="mb-4">
                    <Form.Label className="project-create-label">Internal Systems</Form.Label>
                    <p className="text-muted mb-1" style={{ fontSize: '0.78rem' }}>One system per line</p>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder={'e.g. Salesforce CRM\nJira\nInternal billing system'}
                        value={entSystems}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEntSystems(e.target.value)}
                        disabled={submitting}
                        className="project-create-input"
                    />
                </Form.Group>

                <Form.Group id="project-create-enterprise-acronyms" className="mb-4">
                    <Form.Label className="project-create-label">Acronyms &amp; Glossary</Form.Label>
                    <p className="text-muted mb-1" style={{ fontSize: '0.78rem' }}>
                        Paste acronyms, abbreviations, and internal terms. One entry per line works best.
                    </p>
                    <Form.Control
                        as="textarea"
                        rows={5}
                        placeholder={'CRM: Customer relationship management\nRPA - Robotic process automation\nSLA, Service-level agreement'}
                        value={entAcronyms}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEntAcronyms(e.target.value)}
                        disabled={submitting}
                        className="project-create-input"
                    />
                </Form.Group>

                <Form.Group id="project-create-enterprise-process" controlId="project-create-enterprise-process-input" className="mb-4">
                    <Form.Label className="project-create-label">Internal Process Notes</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Describe internal workflows, approval chains, or processes relevant to testing"
                        value={entProcessNotes}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEntProcessNotes(e.target.value)}
                        disabled={submitting}
                        className="project-create-input"
                    />
                </Form.Group>

                <Form.Group id="project-create-enterprise-testing" controlId="project-create-enterprise-testing-input" className="mb-4">
                    <Form.Label className="project-create-label">Testing Cautions</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Anything the AI agent should be aware of when generating tests, e.g. known system quirks, environments, or restricted areas"
                        value={entTestingNotes}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEntTestingNotes(e.target.value)}
                        disabled={submitting}
                        className="project-create-input"
                    />
                </Form.Group>

                <div id="project-create-enterprise-documents" className="mb-1">
                    <Form.Label className="project-create-label mb-0">Upload Enterprise Documents</Form.Label>
                    <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                        Internal documents such as SOPs, architecture guides, or internal wikis (PDF, DOCX, TXT, or Markdown).
                    </p>
                    <FileDropZone
                        files={enterpriseFiles}
                        onFilesChange={setEnterpriseFiles}
                        disabled={submitting}
                        dropZoneId="project-create-enterprise-documents-dropzone"
                    />
                </div>

                <div className="wizard-step-footer">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => goToStep(2)}
                        disabled={submitting}
                        className="project-create-back-btn"
                    >
                        ← Back
                    </Button>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Button
                            id="project-create-step3-next"
                            variant="primary"
                            onClick={() => goToStep(4)}
                            disabled={submitting}
                            className="project-create-submit-btn"
                        >
                            Next →
                        </Button>
                        <button
                            type="button"
                            className="wizard-skip-link"
                            onClick={() => goToStep(4)}
                            disabled={submitting}
                        >
                            Skip this step →
                        </button>
                    </div>
                </div>
            </>
        );
    }

    /* --- Step 4: Review & Create --- */

    function renderStep4(): React.ReactElement
    {
        const systemsList = entSystems.split('\n').map(s => s.trim()).filter(Boolean);
        const validAcronyms = parseEnterpriseAcronymsText(entAcronyms);

        return (
            <>
                <div className="wizard-section-title">Review &amp; Create</div>
                <div className="wizard-section-subtitle">
                    Review your project details before creating.
                </div>

                {/* Project Basics card */}
                <div id="project-create-review-basics" className="review-section-card">
                    <div className="review-section-card-title">Project Basics</div>
                    <div className="review-row">
                        <span className="review-row-label">Name</span>
                        <span className="review-row-value" style={{ fontWeight: 700, fontSize: '1rem' }}>
                            {name}
                        </span>
                    </div>
                    <div className="review-row">
                        <span className="review-row-label">Type</span>
                        <span className="review-row-value">
                            <Badge bg={projectType === 'azure_devops' ? 'primary' : 'secondary'} style={{ fontSize: '0.75rem' }}>
                                {projectType === 'azure_devops' ? 'Azure DevOps' : 'Simple'}
                            </Badge>
                        </span>
                    </div>
                    {projectType === 'azure_devops' && (
                        <>
                            <div className="review-row">
                                <span className="review-row-label">Organization</span>
                                <span className="review-row-value">{azureOrg}</span>
                            </div>
                            <div className="review-row">
                                <span className="review-row-label">Project</span>
                                <span className="review-row-value">{azureProject}</span>
                            </div>
                            <div className="review-row">
                                <span className="review-row-label">Team</span>
                                <span className="review-row-value">{azureTeam}</span>
                            </div>
                            <div className="review-row">
                                <span className="review-row-label">PAT</span>
                                <span className="review-row-value" style={{ color: '#6b7280' }}>
                                    Configured (&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;)
                                </span>
                            </div>
                        </>
                    )}
                    {domainDescription.trim() && (
                        <div className="review-row" style={{ flexDirection: 'column', gap: 4 }}>
                            <span className="review-row-label">Business Context</span>
                            <span className="review-row-value" style={{ color: '#374151', fontWeight: 400 }}>
                                {domainDescription.length > 200
                                    ? domainDescription.slice(0, 200) + '…'
                                    : domainDescription}
                            </span>
                        </div>
                    )}
                </div>

                {/* Domain Knowledge card */}
                <div id="project-create-review-domain" className="review-section-card">
                    <div className="review-section-card-title">Domain Knowledge</div>
                    <div className="review-row">
                        <span className="review-row-label">Context Notes</span>
                        <span className="review-row-value">
                            {step2Notes.trim() || <span style={{ color: '#9ca3af' }}>None</span>}
                        </span>
                    </div>
                    <div className="review-row">
                        <span className="review-row-label">Documents</span>
                        <span className="review-row-value">
                            {domainFiles.length > 0
                                ? `${domainFiles.length} document${domainFiles.length !== 1 ? 's' : ''} selected`
                                : <span style={{ color: '#9ca3af' }}>No documents</span>
                            }
                        </span>
                    </div>
                </div>

                {/* Enterprise Knowledge card */}
                <div id="project-create-review-enterprise" className="review-section-card">
                    <div className="review-section-card-title">Enterprise Knowledge</div>
                    <div className="review-row">
                        <span className="review-row-label">Overview</span>
                        <span className="review-row-value">
                            {entSummary.trim() || <span style={{ color: '#9ca3af' }}>—</span>}
                        </span>
                    </div>
                    <div className="review-row">
                        <span className="review-row-label">Internal Systems</span>
                        <span className="review-row-value">
                            {systemsList.length > 0
                                ? `${systemsList.length} system${systemsList.length !== 1 ? 's' : ''}`
                                : <span style={{ color: '#9ca3af' }}>—</span>
                            }
                        </span>
                    </div>
                    <div className="review-row">
                        <span className="review-row-label">Acronyms</span>
                        <span className="review-row-value">
                            {validAcronyms.length > 0
                                ? `${validAcronyms.length} entr${validAcronyms.length !== 1 ? 'ies' : 'y'}`
                                : <span style={{ color: '#9ca3af' }}>—</span>
                            }
                        </span>
                    </div>
                    <div className="review-row">
                        <span className="review-row-label">Process Notes</span>
                        <span className="review-row-value">
                            {entProcessNotes.trim() || <span style={{ color: '#9ca3af' }}>—</span>}
                        </span>
                    </div>
                    <div className="review-row">
                        <span className="review-row-label">Testing Cautions</span>
                        <span className="review-row-value">
                            {entTestingNotes.trim() || <span style={{ color: '#9ca3af' }}>—</span>}
                        </span>
                    </div>
                    <div className="review-row">
                        <span className="review-row-label">Documents</span>
                        <span className="review-row-value">
                            {enterpriseFiles.length > 0
                                ? `${enterpriseFiles.length} document${enterpriseFiles.length !== 1 ? 's' : ''} selected`
                                : <span style={{ color: '#9ca3af' }}>No documents</span>
                            }
                        </span>
                    </div>
                </div>

                <p style={{ fontSize: '0.8rem', color: '#6b7280', textAlign: 'center', marginBottom: 8 }}>
                    Domain knowledge will be generated automatically in the background after your project is created.
                </p>

                <div className="wizard-step-footer">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => goToStep(3)}
                        disabled={submitting}
                        className="project-create-back-btn"
                    >
                        ← Back
                    </Button>
                    <Button
                        id="project-create-review-submit"
                        variant="primary"
                        onClick={handleFinalSubmit}
                        disabled={submitting}
                        className="project-create-submit-btn"
                    >
                        {submitting
                            ? <Spinner animation="border" size="sm" />
                            : 'Create Project'
                        }
                    </Button>
                </div>
            </>
        );
    }

    /* ================================================================
       Root render
       ================================================================ */

    return (
        <div className="project-create-card">

            {/* Card header */}
            <div className="project-create-card-header">
                <div>
                    <h2 className="project-create-title">Create Project</h2>
                    <p className="project-create-subtitle">
                        Start with the business and application context that will seed generated domain knowledge for this project.
                    </p>
                </div>
            </div>

            {/* Step indicator */}
            <WizardStepBar currentStep={currentStep} />

            {/* Step content */}
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}

        </div>
    );
}

export default ProjectCreateFormNew;
